import React, { useEffect, useRef, useState } from "react";
import * as Fa from "react-icons/fa";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { toast } from "react-toastify";
import { fetchData } from "../../../Services/GlobalService";
import { GetDateFormat } from "../../../Services/GlobalFunction/GetDateFormat";
import { downloadExcel } from "../../../Services/GlobalFunction/DownLoadExcel";
import { AxiosExcel } from "../../../API/Axios";
import Loader from "../../../Components/Loader";


export type SegmentType = "HDSSD" | "Industrial DB";

const CHANNEL_TYPE_MAP: Record<SegmentType, string> = {
  "HDSSD": "HDSSD",
  "Industrial DB": "IDB",
};

interface ConstructOption {
  label: string;
  value: number;
  startDate: string;
  endDate: string;
}

interface ParameterOption {
  label: string;
  value: number;
}


interface TemplateOption {
  label: string;
  value: string;
}
const TEMPLATE_LIST: TemplateOption[] = [
  { label: "ODT", value: "ODT" },
  { label: "NSV/LP", value: "NSV/LP" },
];

const FORMAT_LIST = [
  { label: "xlsx", value: "xlsx" },
  { label: "csv", value: "csv" },
];

/* ── Template headers per template type ── */
const TEMPLATE_HEADERS: Record<string, Record<string, null>> = {
  "ODT": {
    "DB Code": null,
    "PID": null,
    "Customer Code": null,
    "SKU": null,
    "ODT Value (Lacs)": null,
    "ODT Volume (KL)": null,
  },
  "NSV/LP": {
    "DB Code": null,
    "SKU": null,
    "NSV (Lacs)": null,
    "LP (Lacs)": null,
  },
};

const SIPUpload: React.FC = () => {
  const UserId = localStorage.getItem("UserId")?.toString();
  const getformat = GetDateFormat();

  const ConstructBaseUrl = "DBScoreCard/LBMBrandAndBrandMapping";
  const UploadBaseUrl = "SIPODTUpload/UploadSIPODT";
  const ExportBaseUrl = "SIPODTUpload/ExportALLSIPODT";

  const ConstructEndpoint = {
    GetConstructList: "FetchingConstructData",
    GetParameterList: "FetchingGridData",
  };

  const fileUploadRef = useRef<FileUpload>(null);

  /* ── State ── */
  const [segment, setSegment] = useState<SegmentType>("HDSSD");
  const [selectedConstruct, setSelectedConstruct] = useState<ConstructOption | null>(null);
  const [selectedParameter, setSelectedParameter] = useState<ParameterOption | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<String | null>(null);
  const [templateFormat, setTemplateFormat] = useState<String | null>('xlsx'); // default XLSX

  const [constructList, setConstructList] = useState<ConstructOption[]>([]);
  const [parameterList, setParameterList] = useState<ParameterOption[]>([]);

  const [constructLoading, setConstructLoading] = useState<boolean>(false);
  const [parameterLoading, setParameterLoading] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  /* ── Load constructs on segment change ── */
  useEffect(() => {
    setSelectedConstruct(null);
    setSelectedParameter(null);
    setSelectedTemplate(null);
    setConstructList([]);
    setParameterList([]);
    loadConstructList();
  }, [segment]);

  /* ── Load parameters on construct change ── */
  useEffect(() => {
    setSelectedParameter(null);
    setSelectedTemplate(null);
    setParameterList([]);
    if (selectedConstruct) loadParameterList();
  }, [selectedConstruct]);

  const loadConstructList = async () => {
    setConstructLoading(true);
    try {
      const res = await fetchData(
        ConstructBaseUrl,
        ConstructEndpoint.GetConstructList,
        `ChannelType=${CHANNEL_TYPE_MAP[segment]}`
      );
      setConstructList(
        (res.Data ?? []).map((d: any) => ({
          label: d.Name,
          value: d.Seq,
          startDate: d.StartDate,
          endDate: d.EndDate,
        }))
      );
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load constructs");
    } finally {
      setConstructLoading(false);
    }
  };

  const loadParameterList = async () => {
    if (!selectedConstruct) return;
    setParameterLoading(true);
    try {
      const res = await fetchData(
        ConstructBaseUrl,
        ConstructEndpoint.GetParameterList,
        `ConstarctSeq=${selectedConstruct}&ChannelType=${CHANNEL_TYPE_MAP[segment]}`
      );
      setParameterList(
        (res.Data ?? []).map((d: any) => ({
          label: d.Name,
          value: d.Seq,
        }))
      );
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load parameters");
    } finally {
      setParameterLoading(false);
    }
  };

  /* ── Upload validation ── */
  const validateUpload = (): boolean => {
    if (!selectedConstruct) { toast.warn("Please select a Construct before uploading."); return false; }
    if (!selectedParameter) { toast.warn("Please select a Parameter before uploading."); return false; }
    if (!selectedTemplate) { toast.warn("Please select a Template before uploading."); return false; }
    return true;
  };

  /* ── Download Template ──
     - Guards: template must be selected first
     - Headers differ per template (ODT vs NSV/LP)
     - File extension follows the selected format dropdown (xlsx / csv)
  ── */
  const handleDownloadTemplate = () => {
    if (!selectedTemplate) {
      toast.warn("Please select a Template to download the template file.");
      return;
    }
    debugger;
    const headers = [TEMPLATE_HEADERS[selectedTemplate as string]];
    const fileName = `SIP_${selectedTemplate}_Template_${getformat}.${templateFormat}`;
    downloadExcel(headers, fileName);
  };

  /* ── File Upload ── */
  const handleFileUpload = async (event: any) => {
    if (!validateUpload()) {
      fileUploadRef.current?.clear();
      return;
    }
    setLoader(true);
    try {
      const file = event.files[0];
      const formData = new FormData();
      formData.append("FileContent", file);
      formData.append("ConstructSeq", String(selectedConstruct));
      formData.append("OTDNSV", String(selectedTemplate));
      formData.append("Parameter", String(selectedParameter));
      formData.append("UserId", UserId ?? "");

      const response = await AxiosExcel.post(UploadBaseUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const res = response.data;
      if (res.Status === "Success") {
        toast.success(res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "ErrorFR") {
        toast.warn(res.message ?? res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "Error") {
        toast.warn(res.message ?? res.Message);
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Upload failed");
      fileUploadRef.current?.clear();
    } finally {
      setLoader(false);
    }
  };

  /* ── Export ── */
  const handleExport = async () => {
    if (!selectedConstruct) { toast.warn("Please select a Construct to export."); return; }
    if (!selectedParameter) { toast.warn("Please select a Parameter to export."); return; }
    if (!selectedTemplate) { toast.warn("Please select a Template to export."); return; }

    setExportLoading(true);
    try {
      const res = await fetchData(
        ExportBaseUrl,
        "",
        `OTDNSV=${selectedTemplate}&ParamSeq=${selectedParameter}&ConstructSeq=${selectedConstruct}`
      );
      if (res?.Data?.length > 0) {
        downloadExcel(res.Data, `SIP_ODT_NSV_LP_${getformat}.xlsx`);
      } else {
        toast.warn("No data found.");
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to download file");
    } finally {
      setExportLoading(false);
    }
  };

  if (loader) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">

        {/* ── Breadcrumb ── */}
        <div className="col-lg-12 col-md-12 col-sm-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mx-0">
              <li className="breadcrumb-item">DB SIP</li>
              <li className="breadcrumb-item active" aria-current="page">
                SIP ODT/NSV/LP Upload
              </li>
            </ol>
          </nav>
        </div>

        {/* ── Page title + action buttons ── */}
        <div className="row">
          <div className="col-lg-4 col-md-12 col-sm-12">
            <h3 className="py-2">SIP ODT/NSV/LP Upload</h3>
          </div>
          <div className="col-lg-8 col-md-12 col-sm-12">
            <div className="text-end">
              <div className="d-inline-flex">
                <Dropdown
                  value={templateFormat}
                  onChange={(e) => setTemplateFormat(e.value)}
                  options={FORMAT_LIST}
                  optionLabel="label"
                  style={{
                    width: 100,
                    borderRadius: "0 6px 6px 0",
                    border: "1px solid #ced4da",
                    borderLeft: "none",
                    height: "100%",
                  }}
                />
              </div>
              <button
                type="button"
                className="btn anime-secondary-btn mx-2"
                onClick={handleDownloadTemplate}
              >
                Download Template <Fa.FaDownload className="ms-2" />
              </button>
              <button
                type="button"
                className="btn anime-btn"
                onClick={handleExport}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    Exporting...{" "}
                    <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true" />
                  </>
                ) : (
                  <>Export <Fa.FaFileExport className="ms-2" /></>
                )}
              </button>
            </div>
          </div>
        </div>

        <section className="sales-box">
          <div className="border bg-white p-3 rounded">
            <div className="row">
              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="d-block mb-2">
                  <label className="form-label">Segment</label>
                  <div className="d-flex flex-wrap gap-3 mt-1">
                    {(["HDSSD", "Industrial DB"] as SegmentType[]).map((s) => (
                      <div key={s} className="d-inline-flex align-items-center">
                        <RadioButton
                          inputId={`seg-${s}`}
                          name="segment"
                          value={s}
                          onChange={(e) => setSegment(e.value)}
                          checked={segment === s}
                        />
                        <label htmlFor={`seg-${s}`} className="form-label ms-2 mb-0">{s}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="d-block mb-2">
                  <label className="form-label">Construct Name</label>
                  <Dropdown
                    value={selectedConstruct}
                    onChange={(e) => setSelectedConstruct(e.value)}
                    options={constructList}
                    optionLabel="label"
                    placeholder={constructLoading ? "Loading..." : "Select"}
                    disabled={constructLoading}
                    className="w-100"
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="d-block mb-2">
                  <label className="form-label">Parameter</label>
                  <Dropdown
                    value={selectedParameter}
                    onChange={(e) => setSelectedParameter(e.value)}
                    options={parameterList}
                    optionLabel="label"
                    placeholder={parameterLoading ? "Loading..." : "Select"}
                    disabled={!selectedConstruct || parameterLoading}
                    className="w-100"
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="d-block mb-2">
                  <label className="form-label">Template</label>
                  <Dropdown
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.value)}
                    options={TEMPLATE_LIST}
                    optionLabel="label"
                    placeholder="Select"
                    disabled={!selectedParameter}
                    className="w-100"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row d-flex justify-content-end align-items-end mt-3">
            <div className="col-lg-7 col-md-7 col-sm-12">
              <div className="UploadSection clearfix">
                <div className="conText">
                  <Fa.FaInfoCircle className="icon" /> Select an Excel or CSV File to upload
                </div>
                <FileUpload
                  ref={fileUploadRef}
                  className="clearfix nTopMargin"
                  name="demo[]"
                  accept=".xlsx, .xls, .csv"
                  emptyTemplate={
                    <p className="m-0">Drag and drop files to here to upload.</p>
                  }
                  customUpload
                  uploadHandler={handleFileUpload}

                />
              </div>
            </div>
            <div className="col-lg-5 col-md-5 col-sm-12">
              <div className="text-end">
                <img
                  src={require("../../../Assets/Images/File_Upload.png")}
                  className="File_Upload_Img drop-right"
                  alt="InnoRise"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SIPUpload;